import React, { useEffect, useState } from 'react';
import { ConfirmModal } from '../ds/ConfirmModal';
import { inspectionsApi } from '../services/api';
import type { InspectionQueueResponse, LifecycleState, PipelineBuilding } from '../services/api';
import { budynkiLabel } from '../utils/plural';

/**
 * Migr. 073 (właściciel 21.10: „Zaplanuj do kolejki”): budynek trafia do kolejki BEZ I i II terminu — od razu jest na
 * tabletach (przegląd w toku), a I termin ustawia się sam przy pierwszym lokalu zapisanym w terenie. Kolejka nie jest
 * etapem: budynek z kolejki znika z „Aktywne”, chipów etapów i „Po terminie” (filtr backendu), widać go pod chipem
 * „W kolejce (N)” i w szukaniu we wszystkich etapach — z plakietką „W kolejce”. Z kolejki: „Zaplanuj” (terminy jak
 * zwykle) albo „Usuń z kolejki” (wraca do „Nowy”).
 */

/** Lustro QUEUEABLE_STATES z backend/src/services/inspectionPlanning.js (= PLANNABLE_STATES panelu). */
const QUEUEABLE_STATES: LifecycleState[] = ['NOWY', 'ZAMKNIETY'];

export const canQueue = (b: PipelineBuilding): boolean => QUEUEABLE_STATES.includes(b.lifecycle_state) && !b.queued;
export const isQueued = (b: PipelineBuilding): boolean => !!b.queued && !!b.inspectionId;

/** Plakietka w kolumnie „Adres”: „W kolejce” — widoczna pod chipem i w szukaniu we wszystkich etapach. */
export function QueuedBadge({ row }: { row: PipelineBuilding }) {
  if (!row.queued) return null;
  return (
    <span
      data-testid={`queued-badge-${row.id}`}
      title="W kolejce: bez terminów, budynek jest już na tabletach. I termin ustawi się sam przy pierwszym lokalu zapisanym w terenie. Nie ma go w „Aktywne” ani w etapach."
      style={{
        alignSelf: 'flex-start',
        marginTop: 'var(--space-1)',
        padding: '0 var(--space-2)',
        borderRadius: 'var(--radius-sm)',
        border: 'var(--border-width) solid var(--blue-700)',
        background: 'var(--changed-bg)',
        color: 'var(--text-primary)',
        fontSize: 'var(--text-caption)',
        fontWeight: 'var(--weight-semibold)',
      }}
    >
      W kolejce
    </span>
  );
}

type Mode = 'add' | 'remove';
type Skip = { building: PipelineBuilding; reason: string };

interface QueueModalProps {
  /** „add” = Do kolejki, „remove” = Usuń z kolejki. */
  mode: Mode;
  /** Budynki do przeniesienia; null = okno zamknięte. */
  targets: PipelineBuilding[] | null;
  onClose: () => void;
  /** Po przebiegu (także częściowo udanym) — odświeżenie listy i liczników. */
  onDone: (result: { done: number }) => void;
}

/**
 * „Do kolejki” / „Usuń z kolejki” — wiersz i zbiorczo, jedno wywołanie API dla całego zaznaczenia (backend: każdy budynek
 * we własnej transakcji). Po udanym komplecie okno się zamyka; odmowy (np. „w terenie zapisano już lokale”) zostają
 * w oknie z adresem — operator widzi, czego nie zrobiono.
 */
export function QueueModal({ mode, targets, onClose, onDone }: QueueModalProps) {
  const [busy, setBusy] = useState(false);
  const [skips, setSkips] = useState<Skip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const open = targets !== null;
  useEffect(() => { if (open) { setSkips([]); setError(null); } }, [open]);
  const list = targets || [];
  const add = mode === 'add';

  const submit = async () => {
    if (busy || list.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const ids = list.map((b) => b.id);
      const res: InspectionQueueResponse = add ? await inspectionsApi.queue(ids) : await inspectionsApi.unqueue(ids);
      const byId = new Map(list.map((b) => [b.id, b]));
      const skipped = (res.skipped || [])
        .map((x) => ({ building: byId.get(x.buildingId), reason: x.reason }))
        .filter((x): x is Skip => !!x.building);
      const done = (add ? res.queued : res.removed)?.length ?? 0;
      setSkips(skipped);
      onDone({ done });
      if (skipped.length === 0) onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nieznany błąd');
    } finally {
      setBusy(false);
    }
  };

  const single = list.length === 1;
  const title = add
    ? (single ? 'Dodać do kolejki?' : `Dodać do kolejki ${budynkiLabel(list.length)}?`)
    : (single ? 'Usunąć z kolejki?' : `Usunąć z kolejki ${budynkiLabel(list.length)}?`);
  const confirmLabel = busy
    ? (add ? 'Dodaję…' : 'Usuwam…')
    : (skips.length ? 'Zamknij' : (add ? 'Do kolejki' : 'Usuń z kolejki'));

  return (
    <ConfirmModal
      open={open}
      title={title}
      message={(
        <div data-testid={add ? 'queue-modal' : 'unqueue-modal'}>
          <p style={{ margin: 0 }}>
            {single ? <>Budynek <strong>{list[0]?.address}</strong></> : 'Budynki'}
            {add
              ? <> {single ? 'trafi' : 'trafią'} do kolejki <strong>bez terminów</strong> i od razu będzie widać {single ? 'go' : 'je'} na tabletach. I termin ustawi się sam przy pierwszym lokalu zapisanym w terenie. Terminy możesz też zaplanować później („Zaplanuj”).</>
              : <> {single ? 'wróci' : 'wrócą'} do etapu <strong>Nowy</strong> i {single ? 'zniknie' : 'znikną'} z listy na tabletach. Budynek, w którym w terenie zapisano już lokal, zostaje — przegląd trwa. Lokal zapisany na tablecie bez zasięgu (jeszcze niewysłany) po usunięciu się nie wyśle — usuwaj, gdy nikt tam nie pracuje.</>}
          </p>
          {list.length > 1 && (
            <ul style={{ margin: 'var(--space-3) 0 0', paddingLeft: 'var(--space-5)', maxHeight: 160, overflowY: 'auto' }}>
              {list.map((r) => <li key={r.id}>{r.address}</li>)}
            </ul>
          )}
          {skips.length > 0 && (
            <div role="alert" style={{ marginTop: 'var(--space-3)', color: 'var(--status-deny)', fontSize: 'var(--text-caption)' }}>
              Pominięto: {skips.length}
              <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-5)' }}>
                {skips.map((f) => <li key={f.building.id}>{f.building.address}: {f.reason}</li>)}
              </ul>
            </div>
          )}
          {error && (
            <div role="alert" style={{ marginTop: 'var(--space-3)', color: 'var(--status-deny)', fontSize: 'var(--text-caption)' }}>
              {error}
            </div>
          )}
        </div>
      )}
      confirmLabel={confirmLabel}
      confirmDisabled={busy || list.length === 0}
      destructive={!add && skips.length === 0}
      onConfirm={() => { if (skips.length) onClose(); else void submit(); }}
      onCancel={() => { if (!busy) onClose(); }}
    />
  );
}
